import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  FileText,
  Buildings,
  AddressBook,
  MagnifyingGlass,
  CheckSquare,
  Square,
  Minus,
  X,
  LinkBreak,
  Link,
  CaretDown,
} from '@phosphor-icons/react';
import { BuyerContact, CollectionActivityReference } from '@dill/dill-shared';
import { useAppDispatch, useAppSelector } from '../../../../../redux/store';
import { getGroupedSuppliersInvoices } from '../../../../../redux/services/invoicesService';

interface ActivityReferenceSelectorProps {
  buyerId: string;
  value: CollectionActivityReference[];
  onChange: (references: CollectionActivityReference[]) => void;
  isDisabled?: boolean;
  label?: string;
  isRequired?: boolean;
  errorText?: string;
  /** Restrict selectable types. Defaults to all types. */
  allowedTypes?: Array<'INVOICE' | 'JOB' | 'CONTACT'>;
  contacts?: BuyerContact[];
  selectedLabel?: string;
}

interface OptionItem {
  value: string;
  label: string;
  secondaryLabel?: string;
  reference: CollectionActivityReference;
  type: 'INVOICE' | 'JOB' | 'CONTACT';
}

type CategoryFilter = 'ALL' | 'INVOICE' | 'JOB' | 'CONTACT';

function ActivityReferenceSelector({
  buyerId,
  value,
  onChange,
  isDisabled = false,
  label = '',
  isRequired = false,
  errorText = '',
  allowedTypes,
  contacts,
  selectedLabel,
}: ActivityReferenceSelectorProps) {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const { groupedSuppliersInvoices, loadingList } = useAppSelector((state) => state.invoice);

  const isLoading = loadingList.includes('getGroupedSuppliersInvoices');
  const [isOpen, setIsOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState<CategoryFilter>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });

  const triggerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const POPOVER_WIDTH = 420;

  useEffect(() => {
    if (currentSupplier?.id && buyerId) {
      dispatch(
        getGroupedSuppliersInvoices({
          supplierId: currentSupplier.id,
          buyerId,
          weeksLimit: 52,
        })
      );
    }
  }, [currentSupplier?.id, buyerId, dispatch]);

  // Popover positioning + click-outside
  useEffect(() => {
    let animationFrameId: number;

    const updatePosition = () => {
      if (!isOpen || !triggerRef.current) return;

      const rect = triggerRef.current.getBoundingClientRect();
      const popoverHeight = popoverRef.current?.offsetHeight || 320;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      const shouldOpenUpward = spaceBelow < popoverHeight + 20 && spaceAbove > spaceBelow;

      setPopoverPosition({
        top: shouldOpenUpward ? rect.top - popoverHeight - 8 : rect.bottom + 6,
        left: Math.max(8, Math.min(rect.left, window.innerWidth - POPOVER_WIDTH - 8)),
      });

      animationFrameId = requestAnimationFrame(updatePosition);
    };

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedOutsidePopover = popoverRef.current && !popoverRef.current.contains(target);
      const clickedOutsideTrigger = triggerRef.current && !triggerRef.current.contains(target);

      if (clickedOutsidePopover && clickedOutsideTrigger) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      animationFrameId = requestAnimationFrame(updatePosition);
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
    };
  }, [isOpen]);

  const options: OptionItem[] = useMemo(() => {
    const invoiceMap = new Map<
      string,
      { docNumber: string; balance?: number; amount?: number | null }
    >();
    const jobMap = new Map<string, { id: string; jobName: string; jobNumber?: string }>();

    for (const supplier of groupedSuppliersInvoices) {
      const invoices = supplier.invoices;
      if (!invoices) continue;

      const groups = [
        invoices.pastDueInvoices?.invoices,
        invoices.currentInvoices?.invoices,
        invoices.paidInvoices?.invoices,
        invoices.todayInvoices?.invoices,
        invoices.pastSignedInvoices?.invoices,
        invoices.pendingPayInvoices?.invoices,
      ];

      for (const group of groups) {
        if (!group) continue;
        for (const inv of group) {
          if (!invoiceMap.has(inv.id)) {
            invoiceMap.set(inv.id, {
              docNumber: inv.docNumber,
              balance: inv.balance,
              amount: inv.amount,
            });
          }
          if (inv.quickBooksJob && inv.quickBooksJob.id && !jobMap.has(inv.quickBooksJob.id)) {
            jobMap.set(inv.quickBooksJob.id, {
              id: inv.quickBooksJob.id,
              jobName: inv.quickBooksJob.jobName,
              jobNumber: inv.quickBooksJob.jobNumber || undefined,
            });
          }
        }
      }
    }

    const invoiceItems: OptionItem[] = Array.from(invoiceMap.entries()).map(([id, data]) => ({
      value: `${id}`,
      label: `${data.docNumber}`,
      secondaryLabel:
        data.balance !== undefined && data.balance !== null
          ? `$${data.balance.toFixed(2)}`
          : undefined,
      reference: {
        id,
        type: 'INVOICE' as const,
        description: `${data.docNumber}`,
        amount: data.balance,
      },
      type: 'INVOICE' as const,
    }));

    const jobItems: OptionItem[] = Array.from(jobMap.values()).map((job) => ({
      value: `JOB-${job.id}`,
      label: job.jobName,
      secondaryLabel: job.jobNumber || undefined,
      reference: {
        id: job.id,
        type: 'JOB' as const,
        description: job.jobName,
      },
      type: 'JOB' as const,
    }));

    const contactItems: OptionItem[] = (contacts || [])
      .filter((c) => c.id)
      .map((c) => {
        const name = `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.email || 'Unknown';
        return {
          value: `CONTACT-${c.id}`,
          label: name,
          secondaryLabel: c.email || c.phoneNumber || undefined,
          reference: {
            id: c.id,
            type: 'CONTACT' as const,
            description: name,
          },
          type: 'CONTACT' as const,
        };
      });

    return [...invoiceItems, ...jobItems, ...contactItems];
  }, [groupedSuppliersInvoices, contacts]);

  const allOptions = options;
  const optionsFilteredByType = useMemo(() => {
    if (!allowedTypes || allowedTypes.length === 0) return allOptions;
    const typeSet = new Set(allowedTypes);
    return allOptions.filter((o) => typeSet.has(o.type));
  }, [allOptions, allowedTypes]);

  const invoiceCount = useMemo(
    () => optionsFilteredByType.filter((o) => o.type === 'INVOICE').length,
    [optionsFilteredByType]
  );
  const jobCount = useMemo(
    () => optionsFilteredByType.filter((o) => o.type === 'JOB').length,
    [optionsFilteredByType]
  );
  const contactCount = useMemo(
    () => optionsFilteredByType.filter((o) => o.type === 'CONTACT').length,
    [optionsFilteredByType]
  );

  const selectedValueSet = useMemo(() => new Set(value.map((r) => `${r.type}-${r.id}`)), [value]);

  const baseFilteredOptions = useMemo(() => {
    let filtered = optionsFilteredByType;
    if (activeFilter === 'INVOICE') {
      filtered = filtered.filter((o) => o.type === 'INVOICE');
    } else if (activeFilter === 'JOB') {
      filtered = filtered.filter((o) => o.type === 'JOB');
    } else if (activeFilter === 'CONTACT') {
      filtered = filtered.filter((o) => o.type === 'CONTACT');
    }
    return filtered;
  }, [optionsFilteredByType, activeFilter]);

  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return baseFilteredOptions;
    const q = searchQuery.toLowerCase();
    return baseFilteredOptions.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        (o.secondaryLabel && o.secondaryLabel.toLowerCase().includes(q))
    );
  }, [baseFilteredOptions, searchQuery]);

  const handleToggle = useCallback(
    (option: OptionItem) => {
      const key = `${option.reference.type}-${option.reference.id}`;
      if (selectedValueSet.has(key)) {
        onChange(value.filter((r) => `${r.type}-${r.id}` !== key));
      } else {
        onChange([...value, option.reference]);
      }
    },
    [value, onChange, selectedValueSet]
  );

  const allVisibleSelected =
    filteredOptions.length > 0 &&
    filteredOptions.every((o) => selectedValueSet.has(`${o.type}-${o.reference.id}`));
  const someVisibleSelected = filteredOptions.some((o) =>
    selectedValueSet.has(`${o.type}-${o.reference.id}`)
  );

  const handleToggleAll = useCallback(() => {
    if (allVisibleSelected) {
      const visibleKeys = new Set(filteredOptions.map((o) => `${o.type}-${o.reference.id}`));
      onChange(value.filter((r) => !visibleKeys.has(`${r.type}-${r.id}`)));
    } else {
      const existingKeys = new Set(value.map((r) => `${r.type}-${r.id}`));
      const newRefs = filteredOptions
        .filter((o) => !existingKeys.has(`${o.type}-${o.reference.id}`))
        .map((o) => o.reference);
      onChange([...value, ...newRefs]);
    }
  }, [allVisibleSelected, filteredOptions, value, onChange]);

  const handleRemovePill = useCallback(
    (ref: CollectionActivityReference) => {
      onChange(value.filter((r) => `${r.type}-${r.id}` !== `${ref.type}-${ref.id}`));
    },
    [value, onChange]
  );

  const showCategorySidebar = !allowedTypes || allowedTypes.length > 1;

  const categories: { key: CategoryFilter; label: string; count: number }[] = useMemo(() => {
    const cats: { key: CategoryFilter; label: string; count: number }[] = [];
    cats.push({ key: 'ALL', label: 'All', count: optionsFilteredByType.length });
    if (invoiceCount > 0) cats.push({ key: 'INVOICE', label: 'Invoices', count: invoiceCount });
    if (jobCount > 0) cats.push({ key: 'JOB', label: 'Jobs', count: jobCount });
    if (contactCount > 0) cats.push({ key: 'CONTACT', label: 'Contacts', count: contactCount });
    return cats;
  }, [optionsFilteredByType.length, invoiceCount, jobCount, contactCount]);

  const optionLookup = useMemo(() => {
    const map = new Map<string, OptionItem>();
    for (const opt of optionsFilteredByType) {
      map.set(`${opt.type}-${opt.reference.id}`, opt);
    }
    return map;
  }, [optionsFilteredByType]);

  const searchPlaceholder =
    activeFilter === 'INVOICE'
      ? 'Search invoices...'
      : activeFilter === 'JOB'
      ? 'Search jobs...'
      : activeFilter === 'CONTACT'
      ? 'Search contacts...'
      : contactCount > 0
      ? 'Search invoices, jobs, or contacts...'
      : 'Search invoices or jobs...';

  const handleTriggerClick = () => {
    if (isDisabled || isLoading) return;
    setIsOpen((prev) => !prev);
    if (!isOpen) {
      setSearchQuery('');
    }
  };

  return (
    <div className="w-full flex flex-col">
      {(label || isRequired) && (
        <label className="text-xs mb-0.5 text-left whitespace-nowrap font-medium">
          {isRequired && <span className="text-red-500">*</span>}
          {label}
        </label>
      )}
      {/* Trigger area */}
      <div ref={triggerRef}>
        <button
          type="button"
          onClick={handleTriggerClick}
          disabled={isDisabled}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls={isOpen ? 'activity-reference-listbox' : undefined}
          className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border transition-colors text-left ${
            errorText
              ? 'border-[#F04438]'
              : isOpen
              ? 'border-blue-300 bg-blue-50/50 ring-1 ring-blue-200'
              : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
          } ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
          <div className="flex items-center gap-2 min-w-0">
            {value.length > 0 ? (
              <Link size={16} weight="duotone" className="text-blue-500 flex-shrink-0" />
            ) : (
              <LinkBreak size={16} className="text-gray-400 flex-shrink-0" />
            )}
            <span className="text-sm text-gray-700 truncate">
              {value.length > 0
                ? `Associated with ${value.length} ${value.length === 1 ? 'record' : 'records'}`
                : allowedTypes?.length === 1 && allowedTypes[0] === 'INVOICE'
                ? 'Associate with invoices'
                : contactCount > 0
                ? 'Associate with invoices, jobs, or contacts'
                : 'Associate with invoices or jobs'}
            </span>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {value.length > 0 && (
              <span className="text-[10px] font-semibold text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded-full">
                {value.length}
              </span>
            )}
            <CaretDown
              size={14}
              className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            />
          </div>
        </button>
      </div>

      {/* Selected pills (always visible below trigger) */}
      {value.length > 0 && (
        <div className="mt-1.5">
          {selectedLabel && (
            <span className="text-2xs font-medium text-gray-500 block mb-1">{selectedLabel}</span>
          )}
          <div className="flex flex-wrap gap-1.5">
            {value.map((ref, idx) => {
              const opt = optionLookup.get(`${ref.type}-${ref.id}`);
              const pillLabel = ref.description || opt?.label || ref.id;
              return (
                <span
                  key={`${ref.type}-${ref.id}-${idx}`}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${
                    ref.type === 'INVOICE'
                      ? 'bg-purple-50 text-purple-700 border border-purple-200'
                      : ref.type === 'CONTACT'
                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                      : 'bg-blue-50 text-blue-500 border border-blue-200'
                  }`}>
                  {ref.type === 'INVOICE' ? (
                    <FileText size={10} weight="fill" />
                  ) : ref.type === 'CONTACT' ? (
                    <AddressBook size={10} weight="fill" />
                  ) : (
                    <Buildings size={10} weight="fill" />
                  )}
                  {pillLabel}
                  {!isDisabled && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemovePill(ref);
                      }}
                      className="ml-0.5 hover:opacity-70">
                      <X size={10} />
                    </button>
                  )}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {errorText && <p className="mt-0.5 text-[0.6em] font-normal text-red-500">{errorText}</p>}

      {/* Popover panel */}
      {isOpen &&
        ReactDOM.createPortal(
          <div
            ref={popoverRef}
            style={{
              position: 'fixed',
              top: `${popoverPosition.top}px`,
              left: `${popoverPosition.left}px`,
              width: POPOVER_WIDTH,
              zIndex: 9999,
            }}
            role="listbox"
            id="activity-reference-listbox"
            aria-label="Associate records"
            className="bg-white rounded-xl border border-gray-200 shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="px-3 py-2 border-b border-gray-100 bg-gray-50/80">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Associate Records
              </span>
            </div>

            <div className="flex">
              {/* Left sidebar - categories (hidden when only one type allowed) */}
              {showCategorySidebar && (
                <div className="w-28 border-r border-gray-100 bg-gray-50/50 flex-shrink-0 py-1">
                  {categories.map((cat) => (
                    <button
                      key={cat.key}
                      type="button"
                      onClick={() => {
                        setActiveFilter(cat.key);
                        setSearchQuery('');
                      }}
                      className={`w-full flex items-center justify-between px-3 py-1.5 text-xs transition-colors ${
                        activeFilter === cat.key
                          ? 'bg-blue-50 text-blue-700 font-medium'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}>
                      <span>{cat.label}</span>
                      <span
                        className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                          activeFilter === cat.key
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-gray-200 text-gray-500'
                        }`}>
                        {cat.count}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {/* Right panel - search + list */}
              <div className="flex-1 flex flex-col min-w-0">
                {/* Search bar + select all */}
                <div className="p-2 border-b border-gray-100 flex items-center gap-2">
                  <div className="relative flex-1">
                    <MagnifyingGlass
                      size={14}
                      className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
                    />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                          setIsOpen(false);
                        }
                      }}
                      placeholder={searchPlaceholder}
                      aria-label={searchPlaceholder}
                      className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 placeholder:text-gray-400"
                      autoFocus
                    />
                  </div>
                  {filteredOptions.length > 0 && (
                    <button
                      type="button"
                      onClick={handleToggleAll}
                      className="flex items-center gap-1 px-2 py-1.5 text-[10px] text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors whitespace-nowrap">
                      {allVisibleSelected ? (
                        <CheckSquare size={13} weight="fill" className="text-blue-600" />
                      ) : someVisibleSelected ? (
                        <Minus size={13} weight="bold" className="text-blue-400" />
                      ) : (
                        <Square size={13} className="text-gray-400" />
                      )}
                      {allVisibleSelected ? 'None' : 'All'}
                    </button>
                  )}
                </div>

                {/* Scrollable list */}
                <div className="max-h-44 overflow-y-auto">
                  {isLoading ? (
                    <div className="flex items-center justify-center py-6 text-xs text-gray-400">
                      Loading...
                    </div>
                  ) : filteredOptions.length === 0 ? (
                    <div className="flex items-center justify-center py-6 text-xs text-gray-400">
                      {optionsFilteredByType.length === 0
                        ? allowedTypes?.length === 1 && allowedTypes[0] === 'INVOICE'
                          ? 'No invoices found'
                          : contactCount > 0 || (contacts && contacts.length > 0)
                          ? 'No invoices, jobs, or contacts found'
                          : 'No invoices or jobs found'
                        : 'No results found'}
                    </div>
                  ) : (
                    filteredOptions.map((option) => {
                      const isSelected = selectedValueSet.has(
                        `${option.reference.type}-${option.reference.id}`
                      );
                      return (
                        <button
                          key={option.value}
                          type="button"
                          role="option"
                          aria-selected={isSelected}
                          onClick={() => handleToggle(option)}
                          className={`w-full flex items-center gap-2 px-3 py-1.5 transition-colors text-left ${
                            isSelected ? 'bg-blue-50/60' : 'hover:bg-gray-50'
                          }`}>
                          {isSelected ? (
                            <CheckSquare
                              size={15}
                              weight="fill"
                              className="text-blue-600 flex-shrink-0"
                            />
                          ) : (
                            <Square size={15} className="text-gray-300 flex-shrink-0" />
                          )}
                          <div className="flex items-center gap-1.5 min-w-0 flex-1">
                            {option.type === 'INVOICE' ? (
                              <FileText size={13} className="text-purple-500 flex-shrink-0" />
                            ) : option.type === 'CONTACT' ? (
                              <AddressBook size={13} className="text-emerald-500 flex-shrink-0" />
                            ) : (
                              <Buildings size={13} className="text-blue-500 flex-shrink-0" />
                            )}
                            <span className="text-xs text-gray-700 truncate">{option.label}</span>
                            {option.secondaryLabel && (
                              <span className="text-[10px] text-gray-400 flex-shrink-0">
                                {option.secondaryLabel}
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-3 py-2 border-t border-gray-100 bg-gray-50/80 flex items-center justify-between">
              <span className="text-[11px] text-gray-500">
                {value.length > 0
                  ? `Associated with ${value.length} ${value.length === 1 ? 'record' : 'records'}`
                  : 'No records associated'}
              </span>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="text-[11px] font-medium text-blue-600 hover:text-blue-700 transition-colors">
                Done
              </button>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

export default ActivityReferenceSelector;
